<template>
  <div>
    <!-- RESULTS TABLE HEADING -->
    <v-alert
      v-if="!isNoResults && hasQuoteSummary"
      color="primary"
      border="left"
      colored-border
      class="elevation-1 body-2 pl-6 mb-6"
    >
      <div class="d-md-flex flex-md-nowrap align-md-baseline">
        <div class="flex-md-grow-1">
          <slot name="successMessage" />
        </div>
        <results-info-quote-ref
          :quote-ref="quoteRef"
        />
      </div>
    </v-alert>

    <!-- NO RESULTS TABLE HEADING -->
    <v-alert
      v-if="isNoResults"
      color="primary"
      border="left"
      colored-border
      class="elevation-1 body-2 pl-6 mb-6"
    >
      <div class="d-md-flex flex-md-nowrap align-md-baseline">
        <div class="flex-md-grow-1">
          <span
            v-if="insuredName"
            class="wrap-words"
          >
            {{ insuredName }}, unfortunately
          </span>
          <span v-else>
            Unfortunately
          </span>
          we were unable to find any quotes using the details entered.
          <template v-if="!isCTAHidden">
            <br>
            To try again, select Edit quote details below.
            <br>
            <br>
            <v-btn
              class="font-weight-bold"
              @click.prevent="$emit('go-to-form')"
            >
              Edit quote details
            </v-btn>
          </template>
        </div>
        <results-info-quote-ref
          :quote-ref="quoteRef"
        />
      </div>
    </v-alert>

    <!-- ADDITIONAL GENERIC HEADLINE -->
    <results-info-additional-generic-headline
      v-if="showAdditionalGenericHeadline"
    />

    <p
      v-show="!isNoResults && !isDetailsHidden"
      class="body-2 mb-4"
    >
      <template v-if="!isResultQtyChanged">
        <span
          v-if="customInfoMessage"
          v-html="customInfoMessage"
        />
        <template v-else>
          We have found you <strong>{{ quotesQty }}</strong> {{ pluralize('quote', quotesQty) }}
        </template>
      </template>
      <template v-else>
        Displaying <strong>{{ quotesQty }}</strong> of <strong>{{ allResultsQty }}</strong> quotes
      </template>

      <span v-if="telematicsQuantity">
        including
        <strong>{{ telematicsQuantity }}</strong>
        &nbsp;
        <a
          href="#"
          @click.prevent="$emit('open-drawer-details', ['details-description', 'telematics'])"
        >telematics</a> quotes
      </span>

      <template v-if="selectedFilters.length">
        with
        <strong>
          {{ headline }}
        </strong>
        <span>selected</span>
      </template>
    </p>
  </div>
</template>

<script>
import pluralize from 'pluralize';
import {
  startCase,
} from 'lodash';
import ResultsInfoQuoteRef from '@/components/car/Results/ResultsInfoQuoteRef';
import ResultsInfoAdditionalGenericHeadline from '@/components/car/Results/ResultsInfoAdditionalGenericHeadline';

export default {
  name: 'results-info',
  components: {
    ResultsInfoQuoteRef,
    ResultsInfoAdditionalGenericHeadline,
  },
  props: {
    quoteRef: {
      type: String,
      default: null,
    },
    insuredName: {
      type: String,
      default: '',
    },
    isNoResults: {
      type: Boolean,
      required: true,
    },
    telematicsQuantity: {
      type: Number,
      default: 0,
    },
    allResultsQty: {
      type: Number,
      default: 0,
    },
    quotesQty: {
      type: Number,
      required: true,
    },
    hasQuoteSummary: {
      type: Boolean,
      required: true,
    },
    selectedFilters: {
      type: Array,
      default: () => [],
    },
    isDetailsHidden: {
      type: Boolean,
      default: false,
    },
    isCTAHidden: {
      type: Boolean,
      default: false,
    },
    customInfoMessage: {
      type: String,
      default: '',
    },
  },
  inject: [
    'constants',
    'productName',
    'config',
    'helpers',
  ],
  computed: {
    isResultQtyChanged () {
      return this.allResultsQty !== this.quotesQty;
    },
    showAdditionalGenericHeadline () {
      return this.helpers.isQuotezonePartner(this.config.partner) &&
        ['car', 'bike', 'van', 'home'].includes(this.productName);
    },
    headline () {
      const labels = this.selectedFilters.map((item) => {
        const label = item.infoLabel || item.label;
        const splittedLabel = label?.split(' ');

        // exclude conjunctions to be capitalized
        if (splittedLabel?.some(str => ['and'].includes(str))) {
          return splittedLabel.map(str => (['and'].includes(str) && str) || startCase(str)).join(' ');
        }

        return startCase(label);
      });

      if (labels.length > 1) {
        const lastLabel = labels.slice(-1);
        const restLabels = labels.slice(0, labels.length - 1);

        return restLabels.join(', ') + ' & ' + lastLabel;
      }

      return labels?.[0];
    },
  },
  setup () {
    return {
      pluralize,
    };
  },
};
</script>
